import {createSlice, PayloadAction} from "@reduxjs/toolkit";

interface User {
  id: number;
  email: string;
  token?: string;
  expiresAt?: number;

  phone?: string | null;
  marketingEmailAgree?: boolean;
  marketingSmsAgree?: boolean;

  role?: string | null;

  googleConnected?: boolean;
  kakaoConnected?: boolean;
  naverConnected?: boolean;

  isActive?: boolean;
}

export interface SignupConsentVersions {
  terms: string;
  privacy: string;
  disclaimer: string;
  ageOver14: string;
  marketing: string;
}

export interface SignupPayload {
  email: string;
  password: string;
  phone?: string;

  agreeTerms: boolean;
  agreePrivacy: boolean;
  agreeDisclaimer: boolean;
  ageOver14Agree: boolean;

  marketingEmailAgree: boolean;
  marketingSmsAgree: boolean;

  consentVersions: SignupConsentVersions;
  turnstileToken: string;
}

export interface FindEmailPayload {
  phone: string;
}

export interface FindEmailSuccessPayload {
  maskedEmail: string;
}

export interface RequestResetPayload {
  email: string;
}

export interface RequestSmsCodePayload {
  phone: string;
  purpose: "signup" | "recovery" | "login";
}

export interface VerifySmsCodePayload {
  phone: string;
  code: string;
  purpose: "signup" | "recovery" | "login";
}

export type AuthStatus = "AUTH_CHECKING" | "AUTHENTICATED" | "ANONYMOUS" | "SESSION_EXPIRED";

export interface AuthState {
  token: string | null;
  loading: boolean;

  signupStatus: "idle" | "pending" | "success" | "fail";
  signupError: string | null;
  loginError: string | null;

  user: User | null;
  userInfoFetched: boolean;

  isAuthenticated: boolean;
  authInitialized: boolean;
  authStatus: AuthStatus;
  loginAt: number | null;
  expiresAt: number | null;
  sessionExpired: boolean;

  findEmailLoading: boolean;
  findEmailError: string | null;
  maskedEmail: string | null;

  requestResetLoading: boolean;
  requestResetError: string | null;
  resetRequested: boolean;

  smsSendLoading: boolean;
  smsSendError: string | null;
  smsSent: boolean;

  smsVerifyLoading: boolean;
  smsVerifyError: string | null;
  smsVerified: boolean;

  smsPhone: string | null;
  smsPurpose: "signup" | "recovery" | "login" | null;
}

const getStoredNumber = (key: string): number | null => {
  const raw = localStorage.getItem(key);
  if (!raw) return null;
  const n = Number(raw);
  return Number.isFinite(n) ? n : null;
};

const clearAuthLocalStorage = () => {
  localStorage.removeItem("user");
  localStorage.removeItem("token");
  localStorage.removeItem("userId");
  localStorage.removeItem("email");
  localStorage.removeItem("loginAt");
  localStorage.removeItem("expiresAt");
};

const storedToken = localStorage.getItem("token");

const initialState: AuthState = {
  token: storedToken,
  loading: false,

  signupStatus: "idle",
  signupError: null,
  loginError: null,

  // localStorage user is only a cache. /api/user/me is the authentication authority.
  user: null,
  userInfoFetched: false,

  isAuthenticated: false,
  authInitialized: false,
  authStatus: "AUTH_CHECKING",
  loginAt: getStoredNumber("loginAt"),
  expiresAt: getStoredNumber("expiresAt"),
  sessionExpired: false,

  findEmailLoading: false,
  findEmailError: null,
  maskedEmail: null,

  requestResetLoading: false,
  requestResetError: null,
  resetRequested: false,

  smsSendLoading: false,
  smsSendError: null,
  smsSent: false,

  smsVerifyLoading: false,
  smsVerifyError: null,
  smsVerified: false,

  smsPhone: null,
  smsPurpose: null,
};

const authSlice = createSlice({
  name: "auth",
  initialState,
  reducers: {
    loginRequest: (state, _action: PayloadAction<{ email: string; password: string }>) => {
      state.loading = true;
      state.loginError = null;
      state.sessionExpired = false;
      state.authStatus = "AUTH_CHECKING";
    },

    loginSuccess: (state, action: PayloadAction<User>) => {
      const user = action.payload;
      const now = Date.now();
      const expiresAt = user.expiresAt ?? null;

      state.user = user;
      state.token = user.token || null;
      state.loading = false;
      state.loginError = null;
      state.userInfoFetched = true;
      state.isAuthenticated = true;
      state.authInitialized = true;
      state.authStatus = "AUTHENTICATED";
      state.loginAt = now;
      state.expiresAt = expiresAt;
      state.sessionExpired = false;

      try {
        localStorage.setItem("user", JSON.stringify(user));

        if (user.token) {
          localStorage.setItem("token", user.token);
          localStorage.setItem("userId", String(user.id));
        } else {
          localStorage.removeItem("token");
          localStorage.setItem("userId", String(user.id));
        }

        localStorage.setItem("email", user.email);
        localStorage.setItem("loginAt", String(now));

        if (expiresAt) {
          localStorage.setItem("expiresAt", String(expiresAt));
        } else {
          localStorage.removeItem("expiresAt");
        }
      } catch {
        // ignore
      }
    },

    authFailure: (state, action: PayloadAction<string>) => {
      state.loading = false;
      state.user = null;
      state.token = null;
      state.loginError = action.payload;
      state.userInfoFetched = true;

      state.isAuthenticated = false;
      state.authInitialized = true;
      state.authStatus = "ANONYMOUS";
      state.loginAt = null;
      state.expiresAt = null;
      state.sessionExpired = false;

      clearAuthLocalStorage();
    },

    logoutRequest: (state) => {
      state.loading = true;
      state.loginError = null;
    },

    logoutSuccess: (state) => {
      state.user = null;
      state.token = null;
      state.loading = false;
      state.loginError = null;
      state.userInfoFetched = true;

      state.isAuthenticated = false;
      state.authInitialized = true;
      state.authStatus = "ANONYMOUS";
      state.loginAt = null;
      state.expiresAt = null;
      state.sessionExpired = false;

      clearAuthLocalStorage();
    },

    sessionExpiredLogout: (state, action: PayloadAction<string | undefined>) => {
      state.user = null;
      state.token = null;
      state.loading = false;
      state.loginError = action.payload || "세션이 만료되었습니다. 다시 로그인해주세요.";
      state.userInfoFetched = true;

      state.isAuthenticated = false;
      state.authInitialized = true;
      state.authStatus = "SESSION_EXPIRED";
      state.loginAt = null;
      state.expiresAt = null;
      state.sessionExpired = true;

      clearAuthLocalStorage();
    },

    restoreSessionRequest: (state) => {
      // Fail closed while the server verifies /api/user/me. Keep the access token
      // only as a bootstrap credential for axios refresh/retry; do not render a
      // logged-in UI from cached localStorage metadata.
      state.user = null;
      state.loading = true;
      state.userInfoFetched = false;
      state.isAuthenticated = false;
      state.authInitialized = false;
      state.authStatus = "AUTH_CHECKING";
      state.loginError = null;
      state.sessionExpired = false;
    },

    guestSessionInitialized: (state) => {
      state.user = null;
      state.token = null;
      state.loading = false;
      state.loginError = null;
      state.userInfoFetched = true;

      state.isAuthenticated = false;
      state.authInitialized = true;
      state.authStatus = "ANONYMOUS";
      state.loginAt = null;
      state.expiresAt = null;
      state.sessionExpired = false;
    },

    authCheckFailure: (state, action: PayloadAction<string | undefined>) => {
      // A transport/5xx failure is not proof of logout. Stay fail-closed and do
      // not promote cached user metadata to authenticated state.
      state.user = null;
      state.loading = false;
      state.userInfoFetched = false;
      state.isAuthenticated = false;
      state.authInitialized = false;
      state.authStatus = "AUTH_CHECKING";
      state.loginError = action.payload || "로그인 상태를 확인하지 못했습니다. 잠시 후 다시 시도해주세요.";
      state.sessionExpired = false;
    },

    signupRequest: (state, _action: PayloadAction<SignupPayload>) => {
      state.signupStatus = "pending";
      state.signupError = null;
    },

    signupSuccess: (state) => {
      state.signupStatus = "success";
      state.signupError = null;
    },

    signupFailure: (state, action: PayloadAction<string>) => {
      state.signupStatus = "fail";
      state.signupError = action.payload;
    },

    setUserInfo: (state, action: PayloadAction<Omit<User, "token" | "expiresAt">>) => {
      const prevToken = state.token ?? localStorage.getItem("token");
      const prevExpiresAt = state.expiresAt ?? getStoredNumber("expiresAt");

      state.user = {
        ...action.payload,
        ...(prevToken ? {token: prevToken} : {}),
        ...(prevExpiresAt ? {expiresAt: prevExpiresAt} : {}),
      };

      state.token = prevToken;
      state.loading = false;
      state.loginError = null;
      state.userInfoFetched = true;
      state.isAuthenticated = true;
      state.authInitialized = true;
      state.authStatus = "AUTHENTICATED";
      state.sessionExpired = false;

      try {
        localStorage.setItem("user", JSON.stringify(state.user));
        localStorage.setItem("email", action.payload.email);
        localStorage.setItem("userId", String(action.payload.id));

        if (prevToken) {
          localStorage.setItem("token", prevToken);
        }
      } catch {
        // ignore
      }
    },

    userInfoRequest: (state) => {
      state.loading = true;
      state.loginError = null;
      if (!state.isAuthenticated) {
        state.authInitialized = false;
        state.authStatus = "AUTH_CHECKING";
      }
    },

    findEmailRequest: (state, _action: PayloadAction<FindEmailPayload>) => {
      state.findEmailLoading = true;
      state.findEmailError = null;
      state.maskedEmail = null;
    },

    findEmailSuccess: (
        state,
        action: PayloadAction<{ maskedEmail: string | null; message?: string }>
    ) => {
      state.findEmailLoading = false;
      state.findEmailError = null;
      state.maskedEmail = action.payload.maskedEmail;
    },

    findEmailFailure: (state, action: PayloadAction<string>) => {
      state.findEmailLoading = false;
      state.findEmailError = action.payload;
      state.maskedEmail = null;
    },

    requestPasswordResetRequest: (state, _action: PayloadAction<RequestResetPayload>) => {
      state.requestResetLoading = true;
      state.requestResetError = null;
      state.resetRequested = false;
    },

    requestPasswordResetSuccess: (state) => {
      state.requestResetLoading = false;
      state.requestResetError = null;
      state.resetRequested = true;
    },

    requestPasswordResetFailure: (state, action: PayloadAction<string>) => {
      state.requestResetLoading = false;
      state.requestResetError = action.payload;
      state.resetRequested = false;
    },

    requestSmsCodeRequest: (state, action: PayloadAction<RequestSmsCodePayload>) => {
      state.smsSendLoading = true;
      state.smsSendError = null;
      state.smsSent = false;

      state.smsPhone = action.payload.phone;
      state.smsPurpose = action.payload.purpose;

      state.smsVerifyError = null;
      state.smsVerified = false;
    },

    requestSmsCodeSuccess: (state) => {
      state.smsSendLoading = false;
      state.smsSendError = null;
      state.smsSent = true;
    },

    requestSmsCodeFailure: (state, action: PayloadAction<string>) => {
      state.smsSendLoading = false;
      state.smsSendError = action.payload;
      state.smsSent = false;
    },

    verifySmsCodeRequest: (state, action: PayloadAction<VerifySmsCodePayload>) => {
      state.smsVerifyLoading = true;
      state.smsVerifyError = null;

      state.smsPhone = action.payload.phone;
      state.smsPurpose = action.payload.purpose;
    },

    verifySmsCodeSuccess: (state) => {
      state.smsVerifyLoading = false;
      state.smsVerifyError = null;
      state.smsVerified = true;
    },

    verifySmsCodeFailure: (state, action: PayloadAction<string>) => {
      state.smsVerifyLoading = false;
      state.smsVerifyError = action.payload;
      state.smsVerified = false;
    },

    clearRecoveryState: (state) => {
      state.findEmailLoading = false;
      state.findEmailError = null;
      state.maskedEmail = null;

      state.requestResetLoading = false;
      state.requestResetError = null;
      state.resetRequested = false;

      state.smsSendLoading = false;
      state.smsSendError = null;
      state.smsSent = false;

      state.smsVerifyLoading = false;
      state.smsVerifyError = null;
      state.smsVerified = false;

      state.smsPhone = null;
      state.smsPurpose = null;
    },
  },
});

export const {
  loginRequest,
  loginSuccess,
  authFailure,

  logoutRequest,
  logoutSuccess,
  sessionExpiredLogout,
  restoreSessionRequest,
  guestSessionInitialized,
  authCheckFailure,

  signupRequest,
  signupSuccess,
  signupFailure,

  setUserInfo,
  userInfoRequest,

  findEmailRequest,
  findEmailSuccess,
  findEmailFailure,

  requestPasswordResetRequest,
  requestPasswordResetSuccess,
  requestPasswordResetFailure,

  requestSmsCodeRequest,
  requestSmsCodeSuccess,
  requestSmsCodeFailure,

  verifySmsCodeRequest,
  verifySmsCodeSuccess,
  verifySmsCodeFailure,

  clearRecoveryState,
} = authSlice.actions;

export default authSlice.reducer;
